import * as React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils';
// Both hooks are generic despite the calendar-flavoured names — they are the
// repo's only body-portal popover helpers and predate this component.
import {
  useCalendarPopoverStyle,
  useCloseOnOutsideClick,
} from './date-picker-shared';

/** Rendered panel box, for the popover's flip/clamp math. */
const PANEL_WIDTH = 260;
const PANEL_HEIGHT = 280;

/** The four riso inks, the navy, the legacy product blue, a violet and a red. */
const DEFAULT_PRESETS = [
  '#1d5fa7',
  '#157a6e',
  '#b57a32',
  '#a05b7b',
  '#1b2b45',
  '#3c8fc3',
  '#6d4aa0',
  '#b23b3b',
];

/** WCAG AA for normal text. */
const LEGIBLE_RATIO = 4.5;

export interface ColorPickerLabels {
  /** Accessible name of the swatch button that opens the panel. */
  trigger: string;
  hue: string;
  saturation: string;
  /** Shown when white text clears {@link LEGIBLE_RATIO} on the colour. */
  legible: string;
  lowContrast: string;
}

const DEFAULT_LABELS: ColorPickerLabels = {
  trigger: 'Choose colour',
  hue: 'Hue',
  saturation: 'Saturation and brightness',
  legible: 'White text readable',
  lowContrast: 'Low contrast on white',
};

const clamp = (n: number, a: number, b: number) => Math.min(b, Math.max(a, n));

/** `abc` / `#ABC` / `aabbcc` → `#aabbcc`; anything else → `null`. */
export function normalizeHex(input: string | null | undefined): string | null {
  if (!input) return null;
  let h = String(input).trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(h))
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  return /^[0-9a-f]{6}$/i.test(h) ? '#' + h.toLowerCase() : null;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = normalizeHex(hex) || '#000000';
  return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as [
    number,
    number,
    number,
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0'))
      .join('')
  );
}

function hexToHsv(hex: string): { h: number; s: number; v: number } {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b),
    d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max ? d / max : 0, v: max };
}

function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s,
    x = c * (1 - Math.abs(((h / 60) % 2) - 1)),
    m = v - c;
  const t =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return rgbToHex((t[0] + m) * 255, (t[1] + m) * 255, (t[2] + m) * 255);
}

function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG relative-contrast ratio between two hex colours, 1:1 to 21:1. */
export function contrastRatio(a: string, b: string): number {
  const l1 = luminance(a),
    l2 = luminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

export interface ColorPickerProps {
  /** Hex value, 3- or 6-digit, with or without `#`. @default "#1d5fa7" */
  value?: string;
  /** Fires with a normalized lowercase 6-digit hex. */
  onChange?: (hex: string) => void;
  /** Swatch row in the popover. @default the four riso inks + four product colours */
  presets?: string[];
  /** @default "default" */
  size?: 'default' | 'sm';
  /** Show the white-text contrast readout. @default true */
  showContrast?: boolean;
  disabled?: boolean;
  error?: boolean;
  /** Applied to the hex field, so an external `<Label htmlFor>` reaches it. */
  id?: string;
  className?: string;
  labels?: Partial<ColorPickerLabels>;
}

/**
 * Brand-colour field: swatch trigger + popover with a saturation/value area,
 * a hue slider, presets, hex entry and a white-text contrast check.
 *
 * One control rather than the `<input type="color"> + <Input>` pair it
 * replaces — the swatch opens the panel and the hex is typed inline, so the
 * two can never disagree. The panel is portaled to `document.body` (carrying
 * `data-ui-popover`, the repo's hook for portaled panels) so it escapes any
 * `overflow` clipping ancestor.
 */
const ColorPicker = React.forwardRef<HTMLDivElement, ColorPickerProps>(
  (
    {
      value = '#1d5fa7',
      onChange,
      presets = DEFAULT_PRESETS,
      size = 'default',
      showContrast = true,
      disabled,
      error,
      id,
      className,
      labels,
    },
    ref,
  ) => {
    const L = { ...DEFAULT_LABELS, ...labels };
    const safe = normalizeHex(value) || '#000000';
    const [open, setOpen] = React.useState(false);
    const [draft, setDraft] = React.useState(safe);
    const wrapRef = React.useRef<HTMLDivElement>(null);
    const panelRef = React.useRef<HTMLDivElement>(null);
    const areaRef = React.useRef<HTMLDivElement>(null);

    React.useImperativeHandle(ref, () => wrapRef.current as HTMLDivElement);

    const sv = hexToHsv(safe);
    // Hue is derived from the value; greys (s === 0) have no hue of their own,
    // so the last chosen one is kept here or the slider would snap back to red.
    const [greyHue, setGreyHue] = React.useState(sv.h);
    const hue = sv.s > 0.001 ? sv.h : greyHue;

    const popoverStyle = useCalendarPopoverStyle(open, wrapRef, {
      width: PANEL_WIDTH,
      height: PANEL_HEIGHT,
    });
    useCloseOnOutsideClick(open, wrapRef, panelRef, setOpen);

    React.useEffect(() => {
      setDraft(safe);
    }, [safe]);

    React.useEffect(() => {
      if (!open) return;
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setOpen(false);
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }, [open]);

    const commit = (hex: string) => {
      const next = normalizeHex(hex);
      if (next) onChange?.(next);
    };

    const pickFromArea = (clientX: number, clientY: number) => {
      const rect = areaRef.current?.getBoundingClientRect();
      if (!rect) return;
      const s = clamp((clientX - rect.left) / rect.width, 0, 1);
      const v = 1 - clamp((clientY - rect.top) / rect.height, 0, 1);
      commit(hsvToHex(hue, s, v));
    };

    // Pointer events rather than mouse: one path covers mouse, touch and pen,
    // and the implicit capture release makes manual window listeners unnecessary.
    const onAreaPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      areaRef.current?.focus();
      pickFromArea(e.clientX, e.clientY);
    };

    const onAreaPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
      pickFromArea(e.clientX, e.clientY);
    };

    const onAreaKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? 0.1 : 0.02;
      let { s, v } = sv;
      if (e.key === 'ArrowLeft') s = clamp(s - step, 0, 1);
      else if (e.key === 'ArrowRight') s = clamp(s + step, 0, 1);
      else if (e.key === 'ArrowUp') v = clamp(v + step, 0, 1);
      else if (e.key === 'ArrowDown') v = clamp(v - step, 0, 1);
      else return;
      e.preventDefault();
      commit(hsvToHex(hue, s, v));
    };

    const onWhite = contrastRatio(safe, '#ffffff');
    const legible = onWhite >= LEGIBLE_RATIO;

    return (
      <div ref={wrapRef} className={cn('w-full', className)}>
        <div
          data-state={open ? 'open' : 'closed'}
          className={cn(
            'flex w-full items-center gap-0.5 rounded-xl border-2 border-border bg-muted font-body transition-all duration-200 ease-spring',
            size === 'default' ? 'h-12 p-1.5' : 'h-9 p-[5px]',
            !disabled && 'hover:border-foreground/40',
            'data-[state=open]:border-foreground data-[state=open]:shadow-hard',
            error &&
              'border-destructive data-[state=open]:border-destructive data-[state=open]:shadow-[4px_4px_0_hsl(var(--destructive))]',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <button
            type="button"
            disabled={disabled}
            aria-label={L.trigger}
            aria-expanded={open}
            aria-haspopup="dialog"
            onClick={() => setOpen((o) => !o)}
            style={{ backgroundColor: safe }}
            className={cn(
              'shrink-0 rounded-lg border-2 border-foreground/10 shadow-[inset_0_1px_2px_rgb(255_255_255/0.35)] outline-none focus-visible:border-foreground',
              size === 'default' ? 'size-7' : 'size-[22px]',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
          />
          <input
            id={id}
            disabled={disabled}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              commit(e.target.value);
            }}
            onBlur={() => setDraft(safe)}
            onFocus={() => setOpen(true)}
            spellCheck={false}
            autoComplete="off"
            className={cn(
              'min-w-0 flex-1 border-0 bg-transparent px-2 font-mono lowercase tracking-[0.04em] text-foreground outline-none disabled:cursor-not-allowed',
              size === 'default' ? 'text-base' : 'text-sm',
            )}
          />
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            disabled={disabled}
            onClick={() => setOpen((o) => !o)}
            className={cn(
              'flex h-full w-6 shrink-0 items-center justify-center border-0 bg-transparent text-muted-foreground',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
          >
            <ChevronDown className="size-3.5" strokeWidth={2.4} />
          </button>
        </div>

        {open &&
          !disabled &&
          createPortal(
            <div
              ref={panelRef}
              role="dialog"
              data-ui-popover
              style={{ ...popoverStyle, width: PANEL_WIDTH }}
              className="flex animate-fade-in flex-col gap-2.5 rounded-2xl border-2 border-border bg-popover p-3 shadow-hard"
            >
              <div
                ref={areaRef}
                role="slider"
                tabIndex={0}
                aria-label={L.saturation}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(sv.v * 100)}
                aria-valuetext={safe}
                onPointerDown={onAreaPointerDown}
                onPointerMove={onAreaPointerMove}
                onKeyDown={onAreaKeyDown}
                className="relative h-[116px] cursor-crosshair touch-none overflow-hidden rounded-lg border-2 border-border outline-none focus-visible:border-foreground"
                style={{
                  background: `linear-gradient(to top, #000, rgb(0 0 0 / 0)), linear-gradient(to right, #fff, rgb(255 255 255 / 0)), ${hsvToHex(hue, 1, 1)}`,
                }}
              >
                <span
                  className="pointer-events-none absolute size-3.5 rounded-full border-2 border-white shadow-[0_0_0_1px_rgb(0_0_0/0.35)]"
                  style={{
                    left: `calc(${sv.s * 100}% - 7px)`,
                    top: `calc(${(1 - sv.v) * 100}% - 7px)`,
                    background: safe,
                  }}
                />
              </div>

              <div className="relative h-3.5">
                <div
                  className="absolute inset-0 rounded-full border-2 border-border"
                  style={{
                    background:
                      'linear-gradient(to right,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00)',
                  }}
                />
                <input
                  type="range"
                  min={0}
                  max={359}
                  value={Math.round(hue)}
                  aria-label={L.hue}
                  onChange={(e) => {
                    const h = Number(e.target.value);
                    setGreyHue(h);
                    // A grey or black value carries no saturation to keep, so
                    // fall back to a mid tone the hue is actually visible on.
                    commit(hsvToHex(h, sv.s || 0.85, sv.v || 0.7));
                  }}
                  className="absolute inset-0 m-0 w-full cursor-ew-resize opacity-0"
                />
                <span
                  className="pointer-events-none absolute -top-[3px] size-5 rounded-full border-2 border-white shadow-[0_10px_24px_-18px_rgb(15_23_42/0.18),0_0_0_1px_rgb(0_0_0/0.25)]"
                  style={{
                    left: `calc(${(hue / 359) * 100}% - 10px)`,
                    background: hsvToHex(hue, 1, 1),
                  }}
                />
              </div>

              <div
                className="grid gap-1.5"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(presets.length, 8)}, minmax(0, 1fr))`,
                }}
              >
                {presets.map((preset) => {
                  const active = normalizeHex(preset) === safe;
                  return (
                    <button
                      key={preset}
                      type="button"
                      title={preset}
                      aria-label={preset}
                      aria-pressed={active}
                      onClick={() => commit(preset)}
                      style={{ backgroundColor: preset }}
                      className={cn(
                        'aspect-square w-full cursor-pointer rounded-lg border-2 outline-none transition-all duration-200 ease-spring',
                        active
                          ? 'border-foreground shadow-[0_0_0_2px_hsl(var(--background)),0_0_0_4px_hsl(var(--foreground)/0.15)]'
                          : 'border-foreground/10',
                      )}
                    />
                  );
                })}
              </div>

              {showContrast && (
                <div className="flex items-center gap-2 rounded-lg bg-muted px-[9px] py-[7px] font-body text-xs text-muted-foreground">
                  <span
                    className="inline-flex h-[22px] items-center justify-center rounded-full px-2 text-[11px] font-bold text-white"
                    style={{ backgroundColor: safe }}
                  >
                    Aa
                  </span>
                  <span className="flex-1">
                    {legible ? L.legible : L.lowContrast}
                  </span>
                  <span
                    className={cn(
                      'font-mono font-bold',
                      legible ? 'text-riso-teal' : 'text-riso-amber',
                    )}
                  >
                    {onWhite.toFixed(1)}:1
                  </span>
                </div>
              )}
            </div>,
            document.body,
          )}
      </div>
    );
  },
);
ColorPicker.displayName = 'ColorPicker';

export { ColorPicker };
